import React, { useState, useEffect, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Button } from "antd";
import styled from "styled-components";

const icons = Quill.import("ui/icons");

// Custom Undo & Redo Icons
icons.undo = `<svg viewBox="0 0 24 24"><path d="M12 5V1L4 8l8 7V9a5 5 0 0 1 5 5h2a7 7 0 0 0-7-7z"></path></svg>`;
icons.redo = `<svg viewBox="0 0 24 24"><path d="M12 5V1l8 7-8 7V9a5 5 0 0 0-5 5H5a7 7 0 0 1 7-7z"></path></svg>`;

// Exported so other editors (Custom Email) use the same lists: Quill's font / size formats are global.
export const EDITOR_FONTS = [
  "Roboto", "Raleway", "Montserrat", "Lato", "Rubik",
  "Open-Sans", "Poppins", "Nunito",
  "Playfair-Display", "Merriweather", "Georgia",
  "Courier-New", "Roboto-Mono", "Fira-Code", "Source-Code-Pro"
];
export const EDITOR_SIZES = ['10', '12', '14', '16', '18', '20', '24', '28', '32', '36'];

const Font = Quill.import("formats/font");
Font.whitelist = EDITOR_FONTS;
Quill.register(Font, true);

const Size = Quill.import('formats/size');
Size.whitelist = EDITOR_SIZES;
Quill.register(Size, true);

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: ${(props) => props.height};
  border-radius: 5px;
`;

const QuillWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`;

const StyledQuill = styled(ReactQuill)`
  display: flex;
  flex-direction: column;
  height: 100%;

  .ql-toolbar {
    position: sticky;
    top: 0;
    background: white;
    z-index: 100;
  }
  .ql-toolbar .ql-font {
    width: 150px; /* Adjust width as needed */
  }
  .ql-toolbar .ql-size {
    width: 60px; /* Adjust width as needed */
  }

  .ql-container {
    flex: 1;
    overflow-y: auto;
  }

  .ql-editor {
    counter-reset: list-counter; /* Reset for custom numbering */
  }

  .ql-editor ol {
    list-style-type: decimal;
  }

  .ql-editor ol li {
    counter-increment: list-counter;
  }

  .ql-undo.disabled,
  .ql-redo.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .ql-editor ol li::before {
    content: counter(list-counter) ". ";
  }
  .ql-undo:hover svg path,
  .ql-redo:hover svg path {
    stroke: blue; /* Change to blue on hover */
    fill : blue;
  }
  ${Font.whitelist.map((font) => {
    const className = font.replace(/\s/g, "-"); // Replace spaces with hyphens
    return `
      .ql-picker.ql-font .ql-picker-label[data-value="${font}"]::before, 
      .ql-picker.ql-font .ql-picker-item[data-value="${font}"]::before {
        font-family: "${font.replace(/-/g, " ")}";  
        content: "${font.replace(/-/g, " ")}";
      }
      .ql-font-${className} {
        font-family: "${font.replace(/-/g, " ")}";
      }
    `;
  }).join("\n")}

${Size.whitelist
    .map((size) => `
      .ql-snow .ql-picker.ql-size .ql-picker-label[data-value='${size}']::before,
      .ql-snow .ql-picker.ql-size .ql-picker-item[data-value='${size}']::before {
        content: '${size}';
      }
      .ql-size-${size} {
       font-size: ${size}px;
      }
    `)
    .join('\n')}
`;

const StyledButton = styled(Button)`
  margin-top: 10px;
  align-self: flex-end;
`;

function updateUndoRedoButtons(quill) {
  const historyModule = quill.getModule("history");
  const undoButton = document.querySelector(".ql-undo");
  const redoButton = document.querySelector(".ql-redo");

  if (historyModule) {
    // Disable/Enable Undo button
    if (historyModule.stack.undo.length === 0) {
      undoButton?.classList?.add("disabled");
    } else {
      undoButton?.classList?.remove("disabled");
    }

    // Disable/Enable Redo button
    if (historyModule.stack.redo.length === 0) {
      redoButton?.classList?.add("disabled");
    } else {
      redoButton?.classList?.remove("disabled");
    }
  }
}

// function applyDefaultStyles(quill) {
//   const selection = quill.getSelection();
//   if (!selection || selection.index === null || selection.index === undefined) {
//     console.warn("No valid selection found. Skipping applyDefaultStyles.");
//     return;
//   }

//   // Check for cursor without selection (new text entry)
//   if (selection.length === 0) {
//     const format = quill.getFormat();
//     if (!format.font) {
//       quill.format("font", "Roboto");
//     }
//     if (!format.size) {
//       quill.format("size", "14");
//     }
//     return; // No further checks needed for single cursor
//   }

//   // Apply only to unformatted text in selection
//   const startIndex = selection.index;
//   const endIndex = selection.index + selection.length;

//   for (let i = startIndex; i < endIndex; i++) {
//     const charFormat = quill.getFormat(i, 1);
//     if (!charFormat.font) {
//       quill.formatText(i, 1, "font", "Roboto");
//     }
//     if (!charFormat.size) {
//       quill.formatText(i, 1, "size", "14");
//     }
//   }
// }

function applyDefaultStyles(quill) {
  const selection = quill.getSelection();
  if (!selection || selection.index == null) {
    console.warn("No valid selection found. Skipping applyDefaultStyles.");
    return;
  }

  const { index: startIndex, length } = selection;

  if (length === 0) {
    // Cursor only
    const format = quill.getFormat();
    if (!format.font) {
      quill.format("font", "Roboto");
    }
    if (!format.size) {
      quill.format("size", "14");
    }
    return;
  }

  const endIndex = startIndex + length;
  let currentIndex = startIndex;

  while (currentIndex < endIndex) {
    const formats = quill.getFormat(currentIndex, 1);

    let needsFont = !formats.font;
    let needsSize = !formats.size;

    if (!needsFont && !needsSize) {
      currentIndex++;
      continue;
    }

    // Start of unformatted range
    let rangeStart = currentIndex;
    while (
      currentIndex < endIndex &&
      ((!quill.getFormat(currentIndex, 1).font && needsFont) ||
        (!quill.getFormat(currentIndex, 1).size && needsSize))
    ) {
      currentIndex++;
    }

    const rangeLength = currentIndex - rangeStart;
    const formatObj = {};
    if (needsFont) formatObj.font = "Roboto";
    if (needsSize) formatObj.size = "14";

    quill.formatText(rangeStart, rangeLength, formatObj);
  }
}


const RichTextEditor = ({ initialValue = "", onSubmit, loading }) => {
  const [editorContent, setEditorContent] = useState(initialValue);
  const [editorHeight, setEditorHeight] = useState("500px");
  const quillRef = useRef(null);

  useEffect(() => {
    setEditorContent(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const updateHeight = () => {
      setEditorHeight(`${window.innerHeight * 0.7}px`);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    if (!quillRef.current) return;
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
  
      // Add undo and redo handlers
      // quill.getModule("toolbar").addHandler("undo", () => quill.history.undo());
      // quill.getModule("toolbar").addHandler("redo", () => quill.history.redo());

      quill.getModule("toolbar").addHandler("undo", () => {
        quill.history.undo();
        updateUndoRedoButtons(quill);
      });
  
      quill.getModule("toolbar").addHandler("redo", () => {
        quill.history.redo();
        updateUndoRedoButtons(quill);
      });
      quill.on("selection-change", (range) => {
        if (range) {
          applyDefaultStyles(quill);
        }
      });
      
      quill.on("text-change", (delta, oldDelta, source) => {
        if (source === 'user') {
          applyDefaultStyles(quill);
        }
      });
  
      // Initial state
      updateUndoRedoButtons(quill);
  
      // Monitor changes to update buttons
      quill.on("text-change", () => {
        updateUndoRedoButtons(quill);
      });
  
      const applyTooltips = () => {
        const toolbar = quill.container?.parentNode?.querySelector(".ql-toolbar");
        if (!toolbar) return;
  
        const tooltips = {
          ".ql-bold": "Bold (Ctrl + B)",
          ".ql-italic": "Italic (Ctrl + I)",
          ".ql-underline": "Underline (Ctrl + U)",
          ".ql-strike": "Strikethrough",
          ".ql-header": "Header Options",
          ".ql-font": "Font Selection",
          ".ql-size": "Font Size",
          ".ql-list[value='ordered']": "Ordered List",
          ".ql-list[value='bullet']": "Bullet List",
          ".ql-script[value='sub']": "Subscript",
          ".ql-script[value='super']": "Superscript",
          ".ql-align": "Text Alignment",
          ".ql-color": "Text Color",
          ".ql-background": "Background Color",
          ".ql-image": "Insert Image",
          ".ql-undo": "Undo (Ctrl + Z)",
          ".ql-redo": "Redo (Ctrl + Y)",
          ".ql-clean": "Remove Formatting",
          ".ql-indent[value='-1']": "Decrease Indent",
          ".ql-indent[value='+1']": "Increase Indent",
          ".ql-align .ql-picker-item:first-child": "Align Left", 
          ".ql-align .ql-picker-item[data-value='center']": "Align Center",
          ".ql-align .ql-picker-item[data-value='right']": "Align Right",
          ".ql-align .ql-picker-item[data-value='justify']": "Justify"
      
        };
  
        // Apply tooltips
        Object.entries(tooltips).forEach(([selector, tooltip]) => {
          const elements = toolbar.querySelectorAll(selector);
          elements.forEach((el) => el.setAttribute("title", tooltip));
        });
      };
  
      // Apply tooltips initially
      applyTooltips();
  
      // Watch for DOM changes using MutationObserver
      const observer = new MutationObserver(applyTooltips);
      observer.observe(quill.container.parentNode, {
        childList: true,
        subtree: true,
      });
  
      return () => observer.disconnect();
    }
  }, []);
  



  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  const handleSubmit = () => {
    onSubmit(editorContent);
  };

 
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      let selectedImage = null;

      const handleImageClick = (e) => {
        if (e.target.tagName === 'IMG') {
          if (selectedImage) {
            selectedImage.style.border = 'none';
          }

          const img = e.target;
          selectedImage = img;
          img.style.border = '2px solid red';

          const existingRemoveButton = document.querySelector('.image-remove-btn');
          if (existingRemoveButton) existingRemoveButton.remove();

          const removeButton = document.createElement('button');
          removeButton.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 20.5523 18.5523 21 18 21H6C5.44772 21 5 20.5523 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          `;

          removeButton.classList.add('image-remove-btn');
          removeButton.style.position = 'absolute';
          removeButton.style.top = '10px';
          removeButton.style.right = '20px';
          removeButton.style.zIndex = '1000';
          removeButton.style.background = 'white';
          removeButton.style.border = 'none';
          removeButton.style.cursor = 'pointer';
          removeButton.style.padding = '4px';
          removeButton.style.borderRadius = '50%';
          removeButton.style.color = '#000';
          removeButton.title = 'Remove Image';

          removeButton.onmouseover = () => {
            removeButton.style.color = 'blue';
          };

          removeButton.onmouseleave = () => {
            removeButton.style.color = '#000';
          };

          const toolbar = document.querySelector('.ql-toolbar');
          if (toolbar) toolbar.appendChild(removeButton);

          removeButton.onclick = () => {
            const blot = Quill.find(img);
            if (blot) {
              const index = quill.getIndex(blot);
              quill.deleteText(index, 1);
              removeButton.remove();
              img.remove();
              selectedImage = null;
            }
          };

          const handleClickOutside = (event) => {
            if (!removeButton.contains(event.target) && event.target !== img) {
              removeButton.remove();
              img.style.border = 'none';
              selectedImage = null;
            }
          };

          document.addEventListener('click', handleClickOutside, { once: true, capture: true });
        }
      };

      quill.root.addEventListener('click', handleImageClick);

      return () => {
        quill.root.removeEventListener('click', handleImageClick);
      };
    }
  }, []);
  

  return (
    <EditorContainer height={editorHeight}>
      <QuillWrapper>
        <StyledQuill
          ref={quillRef}
          value={editorContent}
          onChange={handleEditorChange}
          modules={RichTextEditor.modules}
          formats={RichTextEditor.formats}
        />
      </QuillWrapper>

      <StyledButton type="primary" onClick={handleSubmit} loading={loading}>
        Save
      </StyledButton>
    </EditorContainer>
  );
};

// Configure Quill modules and formats
RichTextEditor.modules = {
  toolbar: {
    container: [
      [{ font: Font.whitelist }],
      [{ header: [1, 2, 3, false] }],
      [{ size: Size.whitelist }],
      ["bold", "italic", "underline", "strike"],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ["image"],
      [{ undo: "undo" }, { redo: "redo" }], // Undo & Redo buttons
      ["clean"],
    ],
    handlers: {
      image: function () {
        const quill = this.quill;
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*"); // Accept any image format
        input.click();
    
        input.onchange = async () => {
          const file = input.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target.result;
              const range = quill.getSelection();
              
              // Insert the image
              quill.insertEmbed(range.index, "image", base64, 'user');
    
              // Automatically tracked by history for undo/redo
              quill.setSelection(range.index + 1); 
            };
            reader.readAsDataURL(file);
          }
        };
      },
      undo: function () {
        const quill = this.quill;
        const historyModule = quill.getModule('history');

        if (historyModule) {
          historyModule.undo();
        
        }
      },
      redo: function () {
        const quill = this.quill;
        const historyModule = quill.getModule('history');
        if (historyModule) {
          historyModule.redo();
        }
      },
    }
  },
  // imageResize: {
  //   parchment: Quill.import("parchment"),
  //   modules: ["Resize", "DisplaySize"],
  // },
  history: {
    delay: 2000,
    maxStack: 500,
    userOnly: true,
  },
};

RichTextEditor.formats = [
  "font",
  "header",
  'size',
  "bold",
  "italic",
  "underline",
  "strike",
  "script",
  "list",
  "bullet",
  "indent",
  "align",
  "color",
  "background",
  "blockquote",
  "code-block",
  "direction",
  "image",
];

export default RichTextEditor;


